import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Spinner,
  Container,
  Row,
  Col,
} from "react-bootstrap";
import axios from "./axiosConfig";

interface TenantMetricValueQueryItem {
  name: string;
  value_query: string | null;
  found_in_prometheus: boolean;
  remark?: string;
}

interface TenantMetricValueQueryResult {
  tenant_id: number;
  count: number;
  items: TenantMetricValueQueryItem[];
}

interface TenantMetricCurrentValueResponse {
  tenant_id: number;
  name: string;
  value_query: string;

  result_type: string;
  result: any;

  raw_value?: string;
  mapped_value?: string;
  display_value?: string;
  mapping_applied: boolean;
}

type ValueState = {
  raw?: string;
  mapped?: string;
  display?: string;
  applied?: boolean;
};

const PAGE_MAX_WIDTH = 1200;

const TenantMetricValueQueries: React.FC = () => {
  const [items, setItems] = useState<TenantMetricValueQueryItem[]>([]);

  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedName, setSelectedName] = useState<string>("");

  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);

  const [valueQuery, setValueQuery] = useState<string>("");
  const [currentValue, setCurrentValue] = useState<ValueState | null>(null);

  const didInit = useRef(false);

  const fetchList = async () => {
    setLoadingList(true);
    setListError(null);

    try {
      const resp = await axios.get<TenantMetricValueQueryResult>(
        "/api/tenant/metric-value-queries"
      );

      const list = resp.data.items || [];
      setItems(list);

      if (selectedName) {
        const stillExists = list.some((x) => x.name === selectedName);
        if (!stillExists) {
          setSelectedName("");
          setValueQuery("");
          setCurrentValue(null);
          setQueryError(null);
        }
      }
    } catch (e: any) {
      setListError(
        e?.response?.data?.message || e?.message || "Failed to load list."
      );
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only Found items appear in dropdown
  const foundItems = useMemo(() => {
    return items.filter((x) => x.found_in_prometheus && x.name);
  }, [items]);

  const selectedItem = useMemo(() => {
    if (!selectedName) return undefined;
    return foundItems.find((x) => x.name === selectedName);
  }, [foundItems, selectedName]);

  const runQuery = async (name: string) => {
    if (!name) return;

    setQueryLoading(true);
    setQueryError(null);

    try {
      const resp = await axios.get<TenantMetricCurrentValueResponse>(
        "/api/tenant/metric-current-value",
        { params: { name } }
      );

      const data = resp.data;

      setValueQuery(data.value_query || selectedItem?.value_query || "");

      setCurrentValue({
        raw: data.raw_value,
        mapped: data.mapped_value,
        display: data.display_value,
        applied: data.mapping_applied,
      });
    } catch (e: any) {
      setQueryError(
        e?.response?.data?.message || e?.message || "Query failed."
      );
      setCurrentValue(null);
    } finally {
      setQueryLoading(false);
    }
  };

  // Selection change:
  // - update query from list
  // - clear value state
  // - auto query once
  useEffect(() => {
    if (selectedItem?.value_query) {
      setValueQuery(selectedItem.value_query);
    } else {
      setValueQuery("");
    }

    setCurrentValue(null);
    setQueryError(null);

    if (!selectedName) {
      didInit.current = true;
      return;
    }

    if (!didInit.current) {
      didInit.current = true;
    }

    runQuery(selectedName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedName, selectedItem?.value_query]);

  const handleManualQuery = async () => {
    await runQuery(selectedName);
  };

  const renderCurrentValueBadge = () => {
    if (queryLoading) {
      return (
        <Badge bg="secondary">
          <Spinner animation="border" size="sm" /> Loading
        </Badge>
      );
    }

    if (!currentValue) {
      return <Badge bg="secondary">No data</Badge>;
    }

    const showValue =
      currentValue.display ?? currentValue.mapped ?? currentValue.raw;

    if (!showValue) {
      return <Badge bg="secondary">No data</Badge>;
    }

    if (currentValue.applied) {
      return <Badge bg="primary">{showValue}</Badge>;
    }

    return <Badge bg="info">{showValue}</Badge>;
  };

  return (
    <Container
      fluid
      className="p-3"
      style={{ maxWidth: PAGE_MAX_WIDTH, margin: "0 auto" }}
    >
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <h4 className="mb-1">Tenant Metric Quick Query</h4>
          <div className="text-muted" style={{ fontSize: 12 }}>
            Pick a metric name to view its PromQL and current value in one compact card.
          </div>
        </div>
        <div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={fetchList}
            disabled={loadingList}
          >
            {loadingList ? "Refreshing..." : "Refresh"}
          </Button>
        </div>
      </div>

      {listError && (
        <Alert variant="danger" className="py-2">
          {listError}
        </Alert>
      )}

      {/* Selector Card */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="align-items-end g-3">
            <Col xs={12} md={8} lg={7}>
              <Form.Group>
                <Form.Label className="mb-1">Name</Form.Label>
                <Form.Select
                  value={selectedName}
                  onChange={(e) => setSelectedName(e.target.value)}
                  disabled={loadingList}
                >
                  <option value="">-- Select a metric name --</option>
                  {foundItems.map((it) => (
                    <option key={it.name} value={it.name}>
                      {it.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xs={12} md={4} lg={5} className="d-flex justify-content-md-end">
              <div className="d-flex gap-2">
                <Badge bg="primary">Found: {foundItems.length}</Badge>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Single compact detail card */}
      {selectedName && (
        <>
          {queryError && (
            <Alert variant="danger" className="py-2">
              {queryError}
            </Alert>
          )}

          <Card>
            <Card.Body>
              <Row className="align-items-center g-3">
                {/* Name */}
                <Col xs={12} md={3} lg={3}>
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    Name
                  </div>
                  <div className="fw-semibold">{selectedName}</div>
                </Col>

                {/* Value Query */}
                <Col xs={12} md={6} lg={6}>
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    Value Query (PromQL)
                  </div>
                  {valueQuery ? (
                    <div
                      style={{
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: 6,
                        padding: "8px 10px",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        lineHeight: 1.35,
                      }}
                    >
                      <code>{valueQuery}</code>
                    </div>
                  ) : (
                    <div className="text-muted">No value query available.</div>
                  )}
                </Col>

                {/* Current Value */}
                <Col xs={12} md={2} lg={2}>
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    Current Value
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {renderCurrentValueBadge()}
                    {currentValue?.applied && (
                      <span className="text-muted" style={{ fontSize: 10 }}>
                        mapped
                      </span>
                    )}
                  </div>
                </Col>

                {/* Action: Query only */}
                <Col
                  xs={12}
                  md={1}
                  lg={1}
                  className="d-flex justify-content-md-end"
                >
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={handleManualQuery}
                    disabled={queryLoading || !selectedName}
                    title="Re-query current value"
                  >
                    Query
                  </Button>
                </Col>
              </Row>

              {selectedItem?.remark && (
                <div className="text-muted mt-2" style={{ fontSize: 11 }}>
                  {selectedItem.remark}
                </div>
              )}

              <div className="text-muted mt-2" style={{ fontSize: 10 }}>
                Auto query triggers on selection. Display order: display_value → mapped_value → raw_value.
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
};

export default TenantMetricValueQueries;
